'use client'
import { useQueryClient } from '@tanstack/react-query'
import styles from './game.module.css'
import PEGI from '@/components/UI/PEGI/PEGI'
import { useLocale, useTranslations } from 'next-intl'
import SupportedControls from '@/components/UI/SupportedControls/SupportedControls'
import LinkPill from '@/components/UI/Buttons/LinkPill/LinkPill'
import Button, { ButtonKind, ButtonSize } from '@/components/UI/Buttons/Button/Button'
import VerticalRow from '@/components/Rows/VerticalRow/VerticalRow'
import Label, { LabelKind } from '@/components/UI/Label/Label'
import IconButton, { IconButtonSize } from '@/components/UI/Buttons/IconButton/IconButton'
import IconVolume from '@/assets/img/Icon_Volume-Max.svg'
import IconVolumeOff from '@/assets/img/Icon_Volume-OFF.svg'
import IconSavedOff from '@/assets/img/Icon_saved-off.svg'
import IconSavedOn from '@/assets/img/Icon_Saved-on.svg'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useExecutionStore } from '@/stores/useExecutionStore'
import AgeModal from '@/components/Modals/AgeModal/AgeModal'
import { useUserStore } from '@/stores/useUserStore'
import GamepadModal from './modals/GamepadModal/GamepadModal'
import { useModalStore } from '@/stores/useModalStore'
import LinkButton, { LinkButtonSize } from '@/components/UI/Buttons/LinkButton/LinkButton'
import { useProfiles } from '@/apiUtilities/queries/useProfiles'
import InfoTooltip from '@/components/UI/Tooltips/InfoTooltip/InfoTooltip'
import FanaticalPopover from '@/components/UI/FanaticalPopover/FanaticalPopover'
import { ModalTypes } from '@/interfaces/api/generic/api_modals_types'
import FanaticalPopup from '@/components/UI/FanaticalPopup/FanaticalPopup'
import WindowsPopup from '@/components/UI/WindowsPopup/WindowsPopup'
import { useAddFavorite } from '@/apiUtilities/mutations/useAddFavorite'
import { EditProfileData } from '@/interfaces/api/profiles/api_request_types'
import { AddFavoriteData, API_Data_Add_Favorite_Response, API_Data_Delete_Favorite_Response } from '@/interfaces/api/favorites/favorites_types'
import Notification from '@/components/Notification/Notification'
import { toast } from 'react-toastify'
import { API_Error } from '@/interfaces/api/response/api_response_types'
import { useDeleteFavorite } from '@/apiUtilities/mutations/useDeleteFavorite'
import FreeTrialExpired from '@/components/UI/FreeTrialExpired/FreeTrialExpired'
import { useSelf } from '@/apiUtilities/queries/useSelf'
import { gameHasHTML5, getHTML5ConfigParams, userHasActivePlan } from '@/utils/functions'
import { redirect, useRouter } from 'next/navigation'
import { useEditProfile } from '@/apiUtilities/mutations/useEditProfile'
import { useLaunchGame } from '@/apiUtilities/mutations/useLaunchGame'
import { API_Data_Launch_Game_Response, LaunchGameData } from '@/interfaces/api/games/api_request_types'
import { CircularProgress, Tooltip } from '@mui/material'
import { useQueueStore } from '@/stores/useQueueStore'
import QueueModal from '@/components/Modals/QueueModal/QueueModal'
import { Genre } from '@/interfaces/genres'
import { RowContentType, RowStyle } from '@/interfaces/rows_types'
import { useGame } from '@/apiUtilities/queries/useGame'
import { GameBrief, GameType } from '@/interfaces/games_types'
import { SourceTypes } from '@/interfaces/subscription_types'
import Icon from '@/components/UI/Icon/Icon'
import Icon_Info from '@/assets/img/Icon_Info.svg'
import Icon_Maintenance from '@/assets/img/Icon_UnderMaintenance.svg'
import { parseDevice, pushToDataLayer } from '@/utils/helpers'
import { Devices } from '@/interfaces/genericTypes.types'
import WindowsModal from './modals/WindowsModal/WindowsModal'
import { useWindowSize } from '@/hooks/useWindowsSize'
import Image from 'next/image'
import { FEATURES, IMAGE_HEIGHT_HORIZONTAL, IMAGE_HEIGHT_VERTICAL, IMAGE_WIDTH_HORIZONTAL, IMAGE_WIDTH_VERTICAL } from '@/utils/constants'
import DTPaymentOnHoldModal from '@/components/Modals/DTPaymentOnHoldModal/DTPaymentOnHoldModal'
import { preload } from 'react-dom'
import { ProfileType } from '@/interfaces/profile_types'
import VPadBanner from '@/components/VPadBanner/VPadBanner'
import { usePadsStore } from '@/stores/usePadsStore'
import { VPadStage } from '@/interfaces/virtualPad_types'
import { useDynamicSignUpURL } from '@/apiUtilities/queries/useDynamicSignUpURL'

export default function Game({ id, ua, initialUserId, initialProfileId } : { id : string, ua?: string, initialUserId?: string, initialProfileId?: string }) {
	const locale = useLocale()
	const userId = useUserStore((s) => s.userId) ?? initialUserId
	const profileId = useUserStore((s) => s.profileId) ?? initialProfileId
	const { showAge18, openModal, closeModal } = useModalStore()
	const virtualGamepads = usePadsStore((state) => state.vpads)
	const [muted, setMuted] = useState(true)
	const queryClient = useQueryClient()
	const { data: game, isSuccess, isError } = useGame({ id, locale, profileId })
	const { getQueue } = useQueueStore()
	const getSelf = useSelf({ enabled: !!userId })
	const getProfiles = useProfiles({ enabled: !!userId })
	const profile = getProfiles.isSuccess ? getProfiles.data.find(p => p.id === profileId): null
	const t = useTranslations('Game')
	const tn = useTranslations('Notifications')
	const te = useTranslations('Errors.GameLaunch')
	const [byolSeen, setByolSeen] = useState(false)
	const [fanaticalSeen, setFanaticalSeen] = useState(false)
	const [forceShowByol, setForceShowByol] = useState(false)
	const [forceShowFanatical, setForceShowFanatical] = useState(false)
	const [ageModalOpen, setAgeModalOpen] = useState<boolean>(false)
	const trailerVideoRef = useRef<HTMLVideoElement>(null)
	const contentTrailerVideoRef = useRef<HTMLVideoElement>(null)

	const launchGame = useLaunchGame()
	const setQueue = useQueueStore(state => state.setQueue)
	const removeQueue = useQueueStore(state => state.removeQueue)
	
	const similarContent: GameBrief[] =
		isSuccess && game && game.similars
		? game.similars.map((similar) => ({
				game_id: similar.game_id,
				title: similar.title,
				thumb_vertical: similar.thumb_vertical,
				thumb_horizontal: similar.thumb_horizontal,
				trailer: similar.trailer ?? '',
				new_entry: similar.new_entry,
				type: similar.type,
				description: similar.description ?? '',
				genres: similar.genres ?? [],
				min_age: similar.min_age ?? 0,
				supported_controls: similar.supported_controls ?? 0,
				recommended: similar.recommended,
				favorite: false,
				blur_for_guests: similar.blur_for_guests
		  }))
		: []

	const similarRow = isSuccess && game && game.similars ? {
		id: 'similar',
		title: t('similarGames'),
		spotlight_text: '',
		subtitle: '',
		content_type: RowContentType.Custom,
		genre: null,
		style: RowStyle.VerticalCards,
		special_page_background: '',
		background: null,
		icon: '',
		show_more: -1,
		show_more_action: '',
		content: similarContent,
		supress_labels: null,
	} : undefined

	let labelKind = undefined
	if(isSuccess && game.recommended) labelKind = LabelKind.RECOMMENDED
	if(isSuccess && game.new_entry) labelKind = LabelKind.NEW
	if(isSuccess && game.type === 'steam') labelKind = LabelKind.STEAM_LICENSE
	if(isSuccess && game.type === 'epic') labelKind = LabelKind.EPIC_LICENSE
	if(isSuccess && game.type === 'battlenet') labelKind = LabelKind.BATTLENET_LICENSE
	/**
	 * If the game requires a minimum age of 18 and the user is not logged in, show the age confirmation modal.
	 * Note: If the user has already confirmed their age 18+, it is not shown again during the session
	 */
	useEffect(() => {
		if(showAge18 && !ageModalOpen && !userId && isSuccess && game.min_age === 18) {
			setAgeModalOpen(true)
			openModal({
				content: <AgeModal onClose={(play: boolean) => {
					setAgeModalOpen(false)

					if(play){
						window.setTimeout(() => {
							trailerVideoRef.current?.play()
							contentTrailerVideoRef.current?.play()
						}, 100)
					}
				}} />,
				closeOnBackdrop: false
			})
		}
	}, [game])

	const isLoggedIn = userId && profileId
	const [labelAnchorEl, setLabelAnchorEl] = useState<HTMLElement | null>(null)
	const [fanaticalAnchorEl, setFanaticalAnchorEl] = useState<HTMLElement | null>(null)
	const labelRef = useRef(null)
	const fanaticalRef = useRef(null)
	const isByol = game !== undefined && (game.type === 'steam' || game.type === 'epic' || game.type === 'battlenet')

	useEffect(() => {
		if (isSuccess && labelKind !== undefined && getProfiles.isSuccess && labelRef.current && !profile?.has_seen_byol_tutorial) {
			setLabelAnchorEl(labelRef.current)
		}
	}, [isSuccess, labelKind, getProfiles.isSuccess])

	useEffect(() => {
		if (isSuccess && getProfiles.isSuccess && fanaticalRef.current && !profile?.has_seen_purchase_link_tutorial) {
			setFanaticalAnchorEl(profile?.has_seen_byol_tutorial ? fanaticalRef.current : labelRef.current)
		}
	}, [isSuccess, getProfiles.isSuccess])

	const showFanatical = Boolean(isSuccess &&
		getProfiles.isSuccess &&
		profile &&
		!profile.has_seen_purchase_link_tutorial &&
		game.purchase_link !== ''
		&& fanaticalAnchorEl
		&& isLoggedIn)
	
	const showByol = Boolean(isSuccess
		&& getProfiles.isSuccess
		&& profile
		&& !profile.has_seen_byol_tutorial && !profile.has_seen_purchase_link_tutorial && game.purchase_link === '' 
		&& isByol
		&& labelAnchorEl
		&& isLoggedIn
		&& !showFanatical)

	const router = useRouter()
		// Cooldown state
	const { cooldown, setCooldown, setExecution, getExecutionTime } = useExecutionStore()
	const GAMEPAD = 4

	function handlePaymentOnHold() {		
		if(getSelf.isSuccess && getSelf.data.purchase.subs_status === 5) {
			openModal({
				type: ModalTypes.NATIVE,
				content: <DTPaymentOnHoldModal />
			})
			return
		}
	}

	function handlePlay(fanaticalPopupShown:boolean, windowsPopupShown:boolean, gamepadPopupShown:boolean) {
		if (!game || !profile) return

		if(game.type === 'offline') {
			router.push('/offline/' + game.game_id)
			return
		} 

		if(!fanaticalPopupShown) {
			if(game.purchase_link !== '') {
				openModal({
					type: ModalTypes.NATIVE,
					content: <FanaticalPopup game={game} onClose={() => {
						closeModal();
						handlePlay(true, false, false)
					}} />
				})
				return
			}
			else return handlePlay(true, false, false)
		}
		
		if(!windowsPopupShown) {
			if(game.type === 'epic') {
				openModal({
					type: ModalTypes.CENTERED,
					content: <WindowsPopup game={game} onClose={() => {
						closeModal()
						handlePlay(true, true, false)
					}} />
				})
				return            
			}
			else return handlePlay(true, true, false)
		}

		if(!gamepadPopupShown) {
			const physicalGamepads = navigator.getGamepads?.() || []
			const physicalGamepadConnected  = Array.from(physicalGamepads).some(Boolean)

			const virtualGamepadConnected = Object.values(virtualGamepads).some(
				(vPad) => vPad.stage === VPadStage.CONNECTED
			)

			const gamepadConnected = physicalGamepadConnected || virtualGamepadConnected

			if(game.supported_controls === GAMEPAD && !gamepadConnected) {
				openModal({
					type: ModalTypes.NATIVE,
					content: <GamepadModal callback={() => {
						closeModal();
						handlePlay(true, true, true)
					}} />
				})
				return
			}

			else return handlePlay(true, true, true)
		}

		const gameData = { 
			data: {
				client_id: "WebBrowser",
				region_code: 'ES',
				lang_code: locale,
				entry_id: game.game_id
			} 
		} as LaunchGameData
		
		launchGame.mutate(
			{ profileId: profileId!, payload: gameData },
			{
				onSuccess: (response: API_Data_Launch_Game_Response) => {
					// Save execution time
					if (profileId) setExecution(profileId, Date.now());
					// Clear queue if exists
					if (profileId) removeQueue(profileId)
					if(game.type === 'epic') handleLaunchEpicGameSuccess(response)
					else handleLaunchGameSuccess(response, profile, game)
				},
				onError: (error: any) => handleLaunchGameError(error, profile, game)
			}
		)
	}

	/**
	 * Handles the succesful launch of an Epic game
	 * 
	 * @param response Response from the API when a game is successfully launched
	 */
	function handleLaunchEpicGameSuccess(response: API_Data_Launch_Game_Response) {
		openModal({
			type: ModalTypes.NATIVE,
			content: <WindowsModal callback={() => closeModal()} />
		})
		setCooldown(30)
		window.location.href=response.exec_url
	}

	/**
	 * Handles the succesful launch of a non-Epic game
	 * 
	 * @param response Response from the API when a game is successfully launched
	 * @param profile Profile of the user launching the game
	 * @param game Information of the game being launched
	 */
	function handleLaunchGameSuccess(response: API_Data_Launch_Game_Response, profile: ProfileType, game: GameType) {
		const execUrl = response.exec_url
		const params = getHTML5ConfigParams(execUrl, profile, game)
		setCooldown(30)
		router.push(`/${locale}/html5?${params}`)
	}

	/**
	 * Handles the error of launching a game
	 * Shows a queue modal if the error is a queue error,
	 * otherwise shows a toast with the corresponding error message
	 * 
	 * @param error Error returned by the API when trying to launch a game
	 * @param profile Profile of the user launching the game
	 * @param game Information of the game being launched
	 */
	function handleLaunchGameError(error: any, profile: ProfileType, game: GameType) {
		// Detect queue error
		const queueErr = error?.error?.errors?.find((e: any) => e.string === 'IrisErrnoUserHasBeenEnqueued');
		if (queueErr && error?.error?.data?.position && error.error?.data?.retry_in) {
			setQueue({
				profile: profile,
				game: game,
				position: error.error.data.position,
				retryIn: error.error.data.retry_in,
				lastTried: Date.now(),
			})

			openModal({
				content: <QueueModal onClose={closeModal}/>
			})
		} else {
			switch(error.error.errors[0].errno) {
				case 178: 
					toast.error(<Notification text={te('multipleDevicesError')} />)
					setCooldown(30)
					break
				case 179:
					toast.error(<Notification text={te('genericGameLaunchError')} />)
					if (profileId) {
						setExecution(profileId, Date.now())
						setCooldown(30)
					}
					break
				case 105:
					toast.error(<Notification text={te('subscriptionExpired')} />)
					queryClient.invalidateQueries({ queryKey: ['self'] })
					queryClient.invalidateQueries({ queryKey: ['profiles'] })
					queryClient.invalidateQueries({ queryKey: ['games'] })
					queryClient.invalidateQueries({ queryKey: ['game'] })
					break
				case 188:
					toast.error(<Notification text={te('noTimeLeft')} />)
					break
				case 299:
					error.error.errors[0].data.description === '0' ? 
					toast.error(<Notification text={te('updatingError')} />) :
					toast.error(<Notification text={te('maintenance')} />)

					queryClient.invalidateQueries({ queryKey: ['game'] })
					break
				case 307:
					toast.error(<Notification text={te('anotherProfilePlaying')} />)
					break
				default:
					toast.error(<Notification text={te('defaultUnknowError')} />)
					if (profileId) {
						setExecution(profileId, Date.now())
						setCooldown(30)
					}
					break
			}
		}
	}

	// Cooldown check on mount
	useEffect(() => {
		if (!profileId) return
		const lastExecution = getExecutionTime(profileId)
		if (lastExecution) {
			const elapsed = Math.floor((Date.now() - lastExecution) / 1000)
			if (elapsed < 30) {
				setCooldown(30 - elapsed)
			}
		}
	}, [profileId])

	// Cooldown timer
	useEffect(() => {
		if (cooldown <= 0) return
		const interval = setInterval(() => {
			setCooldown((prev: number) => {
				if (prev <= 1) {
					clearInterval(interval)
					return 0
				}
				return prev - 1
			})
		}, 1000)
		return () => clearInterval(interval)
	}, [cooldown])

	const addFavorite = useAddFavorite()
	const deleteFavorite = useDeleteFavorite()

	async function handleAddFavoriteSuccess (response: API_Data_Add_Favorite_Response) {
		await queryClient.invalidateQueries({ queryKey: ['profiles'] })
		await queryClient.invalidateQueries({ queryKey: ['game'] })
		toast.success(<Notification text={tn('gameSaved')} />)
	}

	async function handleAddFavoriteError (response: API_Error) {
		toast.error(<Notification text={te('defaultUnknowError')} />)
	}

	async function handleDeleteFavoriteSuccess (response: API_Data_Delete_Favorite_Response) {
		await queryClient.invalidateQueries({ queryKey: ['profiles'] })
		await queryClient.invalidateQueries({ queryKey: ['game'] })
		toast.success(<Notification text={tn('gameRemoved')} />)
	}

	async function handleDeleteFavoriteError (response: API_Error) {
		toast.error(<Notification text={te('defaultUnknowError')} />)
	}

	function handleToggleFavorite() {
		if(game && !game.favorite && profileId) {
			const addFavoriteData: Partial<AddFavoriteData['data']> = {}
			addFavoriteData.entry_id = game.game_id
			addFavorite.mutate({ profileId, payload: { data: addFavoriteData } as AddFavoriteData },
				{
					onSuccess: (response) => handleAddFavoriteSuccess(response),
					onError: (response) => handleAddFavoriteError(response)
				}
			)
		} else if (game && game.favorite && profileId) {
			deleteFavorite.mutate({ profileId, gameId: game.game_id },
				{
					onSuccess: (response) => handleDeleteFavoriteSuccess(response),
					onError: (response) => handleDeleteFavoriteError(response)
				}
			)
		}
	}

	function handleFreeTrialEnd() {
		openModal({
			content: <FreeTrialExpired onClose={closeModal} />
		})
	}

	const gameFromAnotherMembership = isSuccess && (game.playable === undefined || game.playable === null || !game.playable)
	const activeMembership = getSelf.isSuccess && userHasActivePlan(getSelf.data)
	const editProfile = useEditProfile()
	const playButtonLabel = isLoggedIn
								? (isSuccess && isByol) ? t('playExternal') : t('play')
								: (isSuccess && isByol) ? t('getPro') : t('tryForFree')

	useEffect(() => {
		if(isSuccess && getProfiles.isSuccess && isLoggedIn && profile && !profile.has_seen_byol_tutorial && byolSeen) {
			editProfile.mutate({ profileId, payload: { data: { "has_seen_byol_tutorial" : true } } as EditProfileData },
				{
					onSuccess: (response) => queryClient.invalidateQueries({ queryKey: ['profiles'] })
				}
			)
		}
	}, [isLoggedIn, isSuccess, getProfiles.isSuccess, profile, byolSeen])
	
	useEffect(() => {
		if(isSuccess && getProfiles.isSuccess && isLoggedIn && profile && !profile.has_seen_purchase_link_tutorial && fanaticalSeen) {
			editProfile.mutate({ profileId, payload: { data: { "has_seen_purchase_link_tutorial" : true } } as EditProfileData },
				{
					onSuccess: (response) => queryClient.invalidateQueries({ queryKey: ['profiles'] })
				}
			)
		}
	}, [isLoggedIn, isSuccess, getProfiles.isSuccess, profile, fanaticalSeen])

	const device = parseDevice(ua ?? '')
	const gameAvailableInDevice = isSuccess && !((device === Devices.ANDROID || device === Devices.IOS || device === Devices.MAC) && !gameHasHTML5(game))
	
	const [videoCanPlay, setVideoCanPlay] = useState(false)


	useEffect(() => {
	let interval: ReturnType<typeof setInterval>

	const tryAttach = () => {
		const video = contentTrailerVideoRef.current
		if (!video) return

		const handleReady = () => setVideoCanPlay(true)

		video.addEventListener('playing', handleReady)
		video.addEventListener('timeupdate', handleReady)

		if (video.readyState >= 2) {
			setVideoCanPlay(true)
		}

		clearInterval(interval)
	}

	interval = setInterval(tryAttach, 100)

	return () => {
		clearInterval(interval)
	}
	}, [])
	const { data: dynamicSignUpURLData } = useDynamicSignUpURL({
		enabled: FEATURES.signUpExternal && FEATURES.signUpExternalDynamic
	})
	const signUpLink = useMemo(() => {
    	if (!FEATURES.signUp) return '/login'
		if (!FEATURES.signUpExternal) return '/#plans'
		if (!FEATURES.signUpExternalDynamic) {
		  return process.env.NEXT_PUBLIC_FEATURE_SIGNUP_EXTERNAL_LINK ?? ''
		}
		return dynamicSignUpURLData?.url || ''
	  }, [dynamicSignUpURLData])


	if(isError) redirect('/home')
	// console.log('Game: ', game)
	if(game)  preload(process.env.NEXT_PUBLIC_ASSETS_ENDPOINT + game.thumb_horiz_large.esc_200_url, { as: 'image', fetchPriority: 'high' });
	return (
		isSuccess && game &&
		<div className={styles.main} data-testid="Game">
			{/* <video playsInline ref={trailerVideoRef} src={process.env.NEXT_PUBLIC_ASSETS_ENDPOINT + game.trailer} autoPlay={!showAge18 || !!userId || game.min_age < 18 || !ageModalOpen} muted controls={false} loop={true} className={styles.trailer} poster={process.env.NEXT_PUBLIC_ASSETS_ENDPOINT + game.thumb_horiz_large.esc_400_url}/> */}
			{isSuccess && <BackgroundImage game={game} /> }
			<div className={styles.game}>
				<div className={styles.trailerWrapper} data-testid="Game_trailerWrapper" style={{ viewTransitionName: `gameImage_${game.game_id}`}}>
					{!(!isLoggedIn && game.blur_for_guests) && <video data-testid="Game_trailer" playsInline ref={contentTrailerVideoRef} src={process.env.NEXT_PUBLIC_ASSETS_ENDPOINT + game.trailer} autoPlay={!showAge18 ||  !!userId || game.min_age < 18 || !ageModalOpen} muted={muted} controls={false} loop={true} className={`${styles.contentTrailer} ${!isLoggedIn && game.blur_for_guests ? styles.blur : ''}`} poster={process.env.NEXT_PUBLIC_ASSETS_ENDPOINT + game.thumb_horiz_large.esc_200_url} preload='auto'/>}
					{(!isLoggedIn && game.blur_for_guests) && <img src={process.env.NEXT_PUBLIC_ASSETS_ENDPOINT + game.thumb_horiz_large.esc_200_url} data-testid="Game_poster" className={`${styles.contentTrailer} ${!isLoggedIn && game.blur_for_guests ? styles.blur : ''}`} />}
					{videoCanPlay && !(!isLoggedIn && game.blur_for_guests) && <IconButton title="Volume Button" data-testid="Game_trailerSoundButton" icon={muted ? IconVolumeOff : IconVolume} className={styles.volume} onClick={() => setMuted(prev => !prev)}></IconButton>}
				</div>
				<div className={styles.content}>
					<div className={styles.heading}>
						<h1 data-testid="Game_Title">{game.title}</h1>
						{isSuccess && labelKind !== undefined ? <div className={styles.label} ref={labelRef}><Label active={showByol || forceShowByol } kind={labelKind} info={true} callback={() => {
							if(labelKind === LabelKind.BATTLENET_LICENSE || labelKind === LabelKind.STEAM_LICENSE || labelKind === LabelKind.EPIC_LICENSE) {
								setLabelAnchorEl(labelRef.current); setForceShowByol(true)} 
							}}/></div> : null}
					</div>

					<div className={styles.controlsWrapper}>
						{isLoggedIn &&
						<div className={styles.controls} ref={fanaticalRef} data-testid="Game_controls">
							{gameFromAnotherMembership && activeMembership && <LinkButton className={styles.playButton} href={FEATURES.accountSubscription ? '/account-settings/subscription' : '/account-settings/account'} size={LinkButtonSize.LARGE}>{t('upgradeSubscription')}</LinkButton>}
							{!gameFromAnotherMembership && activeMembership && game.status === 0 && <Button className={styles.playButton} size={ButtonSize.LARGE} disabled={true} >{t('updating')}</Button>}
							{!gameFromAnotherMembership && activeMembership && game.status === 1 && cooldown === 0 && !(userId && profileId && getQueue(profileId) && getQueue(profileId)!.game.game_id === game.game_id) &&
							<Button className={styles.playButton} disabled={!gameAvailableInDevice || launchGame.isPending } size={ButtonSize.LARGE} kind={ButtonKind.PRIMARY} onClick={() => {
								pushToDataLayer({
									event: 'game_launch',
									game_id: game.game_id,
									game_name: game.title,
									launch_source: game.type === 'epic' ? 'windows' : 'html5'
								})
								handlePlay(false, false, false)
							}}>
								{playButtonLabel}
							</Button>
							}
							{!gameFromAnotherMembership && activeMembership && game.status === 1 && cooldown === 0 && (userId && profileId && getQueue(profileId) && getQueue(profileId)!.game.game_id === game.game_id) &&
								<Button className={styles.playButton} disabled={!gameAvailableInDevice} size={ButtonSize.LARGE} kind={ButtonKind.PRIMARY} onClick={() => handlePlay(false, false, false)}>
									<div className={styles.buttonQueue}>
										<CircularProgress color={"inherit"} size="1.5rem" />{t('inQueue')}
									</div>
								</Button>
							}

							{!gameFromAnotherMembership && activeMembership && game.status === 1 && cooldown > 0 && 
								<Tooltip 
									title={t('cooldownText')} 
									arrow 
									placement="bottom-start"
									slotProps={{
										tooltip: {
											sx: { maxWidth: 350 }
										}
									}}
								>
									<Button className={styles.playButton} size={ButtonSize.LARGE} disabled={true}>{`00:${cooldown.toString().padStart(2,"0")}`}</Button>
								</Tooltip>
							}
							{!gameFromAnotherMembership && activeMembership && game.status === 2 &&
							<Button className={styles.playButton} size={ButtonSize.LARGE} disabled={true} >{t('maintenance')}</Button>
							}
							{!activeMembership && getSelf.isSuccess && (!getSelf.data.purchase || getSelf.data.purchase.source !== SourceTypes.TRIAL) && (!getSelf.data.purchase || getSelf.data.purchase.subs_status !== 5) && 
							<LinkButton className={styles.playButton} href={'/billing/choose-plan'} internal={true} size={LinkButtonSize.LARGE}>{playButtonLabel}</LinkButton>
							}

							{!activeMembership && getSelf.isSuccess && (getSelf.data.purchase && getSelf.data.purchase.subs_status === 5) && 
							<Button className={styles.playButton} onClick={handlePaymentOnHold} size={ButtonSize.LARGE}>{playButtonLabel}</Button>
							}
							{!activeMembership && getSelf.isSuccess && getSelf.data.purchase && getSelf.data.purchase.source === SourceTypes.TRIAL && 
							<Button className={styles.playButton} size={ButtonSize.LARGE} onClick={handleFreeTrialEnd}>{playButtonLabel}</Button>
							}
							{game.purchase_link && game.purchase_link !== '' && 
							<div className={styles.discountRibbon}><span className='bodyTextSmallStrong'>{t('getADiscount')}</span></div>
							}
						</div>
						}

						{!isLoggedIn && 
						<div className={styles.controls} ref={fanaticalRef}>
							<LinkButton href={signUpLink} size={LinkButtonSize.LARGE}>{playButtonLabel}</LinkButton>
						</div>
						}

						{isLoggedIn && <IconButton title={game.favorite ? "Remove Favorite" : "Add Favorite"} size={IconButtonSize.L} icon={game.favorite ? IconSavedOn : IconSavedOff} className={styles.savedButton} onClick={handleToggleFavorite} />}
					</div>

					{!isLoggedIn && isSuccess &&
					<div className={styles.planInfo} data-testid="Game_planInfo">
						<p className={'bodyTextMedium'}>
							{t.rich(isByol ? 'proPlanInfo' : 'standardPlanInfo', {
                highlighted: (chunks) => <span className={`${styles.highlightedText}`}>{chunks}</span>
							})}
						</p>
					</div>
					}

					{FEATURES.virtualPads && isLoggedIn && <VPadBanner />}

					{game.status === 0 &&
					<div className={styles.maintenance} data-testid="Game_maintenance">
						<Icon icon={Icon_Maintenance} className={styles.maintenanceIcon}/>
						<p className='bodyTextMedium'>{t('updating')}</p>
					</div>
					}

					{game.status === 2 &&
					<div className={styles.maintenance} data-testid="Game_maintenance">
						<Icon icon={Icon_Maintenance} className={styles.maintenanceIcon}/>
						<p className='bodyTextMedium'>{t('maintenance')}</p>
					</div>
					}

					{game.type === 'standalone' &&
					<p className={`bodyTextSmall ${styles.disclaimer}`} data-testid="Game_gameStandalone">
						<Icon icon={Icon_Info}/>
						{t('standalone')}
					</p>
					}

					{!gameAvailableInDevice &&
					<p className={`bodyTextSmall ${styles.disclaimer}`} data-testid="Game_gameNotAvailableInDeviceText">
						<Icon icon={Icon_Info}/>
						{t('gameNotAvailable')}
					</p>
					}

					<div className={styles.data} data-testid="Game_data">
						<div className={styles.pegiWrapper} data-testid="Game_Pegi">
							<span data-testid="Game_gameNotAvailableInDeviceText">{t('minAge')}</span>
							<PEGI pegi={game.min_age} />
						</div>
						<div className={styles.pegiWrapper} data-testid="Game_Players">
							<span>{t('players')}</span>
							<span>{`${game.min_players}${game.max_players !== game.min_players ? ` - ${game.max_players}` : ''}`}</span>
						</div>
						<div className={styles.controls} data-testid="Game_controls">
							<span>{t('supportedControls')}</span>
							<SupportedControls type={game.supported_controls} />
						</div>                    
					</div>
					
					<p data-testid="Game_description">
						{game.description}
					</p>
					
					<div className={styles.genres} data-testid="Game_genres">
						{game.genres.sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })).slice(0,6).map((genre:Genre) => {
							return (
								<LinkPill href={`/catalogue?genre=${genre.id}`} key={genre.id}>{genre.name}</LinkPill>
							)
						})}
					</div>
				</div>
			</div>
			{<div data-testid="Game_similars">
				{similarRow && <VerticalRow row={similarRow} sideGradients={false}/> }
			</div>}
			{(showByol || forceShowByol)
				&& <InfoTooltip
				darkBackground={true}
				anchorEl={labelAnchorEl}
				open={Boolean(labelAnchorEl)}
				onClose={() => { setLabelAnchorEl(null); setByolSeen(true); setForceShowByol(false) }}
				text={t('byolPopupText')}
				buttonLabel={t('byolPopupButton')}
				onAction={() => console.log()}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}/>}
			{showFanatical
				&& <FanaticalPopover
				darkBackground={true}
				anchorEl={fanaticalAnchorEl}
				open={Boolean(fanaticalAnchorEl)}
				onStep={() => setFanaticalAnchorEl(fanaticalRef.current)}
				onClose={() => { setFanaticalAnchorEl(null); setFanaticalSeen(true); setForceShowFanatical(false); }}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				initialStep={profile?.has_seen_byol_tutorial ? 2 : 1}/>}
		</div>
	)
}

type BackgroundImageProps = {
	game: GameType
}

function BackgroundImage({game}: BackgroundImageProps) {
	const { width, height } = useWindowSize()
	return (
		game !== undefined && <Image priority={true} data-testid="Game_backgroundImage" className={styles.trailerImage} alt={game.title} src={process.env.NEXT_PUBLIC_ASSETS_ENDPOINT + (width >= height ? game.thumb_horiz_large.esc_300_url : game.thumb_vertical.esc_300_url)} width={width >= height ? IMAGE_WIDTH_HORIZONTAL : IMAGE_WIDTH_VERTICAL} height={width >= height ? IMAGE_HEIGHT_HORIZONTAL : IMAGE_HEIGHT_VERTICAL}/>
	)
}